<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Das Teilen von 360°-Fotos in sozialen Medien erreicht ein deutlich
            größeres Publikum als eine reine Website-Veröffentlichung – doch
            jede Plattform behandelt 360°-Inhalte unterschiedlich. Facebook
            stellt 360°-Fotos interaktiv dar; Instagram unterstützt keine native
            360°-Anzeige; YouTube unterstützt 360°-Video, aber keine
            360°-Standbilder. Zu verstehen, welche Plattform welche Formate
            unterstützt – und wie Sie die Reichweite auf jeder maximieren –
            bestimmt, ob Ihre 360°-Inhalte Tausende von Zuschauern oder nur eine
            Handvoll erreichen.
          </p>
        </section>

        <section>
          <h2>360°-Foto-Unterstützung auf jeder Plattform</h2>

          <h3>Facebook: Vollständige native 360°-Unterstützung</h3>
          <p>
            Facebook ist die beste Plattform für das Teilen von 360°-Fotos. Wenn
            Sie ein äquirektangulares JPEG mit korrekten XMP Photo
            Sphere-Metadaten hochladen, erkennt Facebook das 360°-Format
            automatisch und stellt es als interaktiven sphärischen Viewer dar.
            Nutzer können direkt in ihrem Newsfeed auf Desktop und Mobilgeräten
            schwenken, neigen und zoomen.
          </p>
          <p>
            <strong>Wie Sie 360°-Fotos auf Facebook posten:</strong> Laden Sie
            die äquirektangulare Quelldatei direkt als Foto-Post hoch (nicht
            über einen Link). Teilen Sie nicht per URL – Facebook aktiviert den
            360°-Viewer nur bei direkt hochgeladenen Bildern. Die Datei muss die
            Google Photo Sphere XMP-Metadaten enthalten. Dateien, die aus
            Travvir, Insta360, Ricoh Theta und der Google Street View App
            exportiert werden, enthalten diese Metadaten automatisch.
          </p>
          <p>
            <strong>Engagement-Wirkung:</strong> 360°-Fotos auf Facebook
            erhalten im Durchschnitt <strong>3× mehr Interaktionen</strong> als
            Standard-Fotos und <strong>4× mehr Shares</strong> – und sind damit
            für die organische Reichweite deutlich effektiver als herkömmliche
            Fotografie.
          </p>

          <h3>Instagram: Keine native 360°-Unterstützung</h3>
          <p>
            Instagram stellt 360°-Fotos nicht als interaktive Inhalte dar. Wenn
            Sie ein äquirektangulares 360°-Bild hochladen, erscheint es als
            flaches, stark verzerrtes Rechteck – die charakteristische
            deformierte Erscheinung einer ungemappten äquirektangularen
            Projektion.
          </p>
          <p>
            <strong>Bester Ansatz für Instagram:</strong> Exportieren Sie einen
            standardmäßigen flachen „Vorschau"-Ausschnitt aus Ihrem 360°-Bild –
            einen Weitwinkel- oder Doppelpanel-Ausschnitt, der die
            interessanteste Ansicht zeigt. Fügen Sie in der Bildunter­schrift
            einen Text hinzu, der Follower zu einem Link weiterleitet (Bio-Link
            oder Stories-Link-Sticker), wo sie die vollständige interaktive Tour
            erleben können. Alternativ können Sie ein kurzes Bildschirmaufnahme-
            Video aufnehmen, das durch die 360°-Tour schwenkt, und es als Reel
            posten – so wird die interaktive Qualität des Inhalts im Videoformat
            von Instagram gezeigt.
          </p>

          <h3>YouTube: Nur 360°-Video (keine Fotos)</h3>
          <p>
            YouTube unterstützt 360°-Video mit vollständiger interaktiver
            Wiedergabe (Nutzer können während der Wiedergabe schwenken und
            neigen), aber keine 360°-Standbilder. Um 360°-Inhalte auf YouTube zu
            teilen, erstellen Sie ein 360°-Video – entweder durch Aufnahme eines
            Zeitraffers oder langsam panning­den Videos im 360°-Modus, oder
            durch Verwendung von Video-Slideshow-Software, um ein rotierendes
            Video aus Ihren 360°-Standbildern zu erstellen.
          </p>

          <h3>Twitter/X: Keine 360°-Unterstützung</h3>
          <p>
            Twitter/X stellt hochgeladene Bilder unabhängig von den Metadaten
            als Standard-Flachbilder dar. Teilen Sie einen überzeugenden
            Ausschnitt oder ein Vorschaubild mit einem Link zur interaktiven
            Version auf Ihrer Website oder Ihrer Travvir-Tour-URL.
          </p>

          <h3>LinkedIn: Keine native 360°-Unterstützung</h3>
          <p>
            LinkedIn stellt 360°-Fotos nicht interaktiv dar. Verwenden Sie den
            gleichen Ansatz wie bei Instagram: Teilen Sie einen Standard-
            Ausschnitt mit einem Link zum vollständigen Erlebnis. LinkedIn ist
            ein nützlicher Verteilungskanal für B2B-Anwendungen von Virtual
            Tours – zum Teilen mit Immobilienfachleuten, Kontakten aus der
            Hotellerie oder potenziellen B2B-Kunden.
          </p>

          <h3>Google Business Profil: Native 360°-Integration</h3>
          <p>
            Das Google Business Profil ist einer der besten 360°-Fotokanäle für
            lokale Unternehmen. Laden Sie direkt über die Google Maps App oder
            Street View App hoch – Fotos erscheinen im Abschnitt „Innenansicht"
            Ihres Business Profils und können lokale Such-Rankings beeinflussen.
            Einen vollständigen Leitfaden hierzu finden Sie in unserem Artikel
            zu
            <a href="/blog/de/google-business-virtual-tour">
              Google Business Virtual Tours</a
            >.
          </p>
        </section>

        <section>
          <h2>Plattform-optimierte Exporte erstellen</h2>

          <h3>Facebook-Exporteinstellungen</h3>
          <p>
            Für den Facebook-Upload: Als äquirektangulares JPEG bei
            <strong>mindestens 6.000 × 3.000 Pixeln</strong> exportieren (höher
            ist besser, aber Facebook komprimiert beim Einlesen). Maximale
            Dateigröße 30 MB. Stellen Sie sicher, dass XMP Photo
            Sphere-Metadaten eingebettet sind – Travvir-Exporte enthalten diese
            automatisch.
          </p>

          <h3>Instagram-Ausschnitte aus 360°-Bildern</h3>
          <p>
            Um einen überzeugenden flachen Ausschnitt aus Ihrem 360°-Bild zu
            extrahieren: Öffnen Sie die 360°-Datei in einem Viewer, navigieren
            Sie zum besten Winkel und machen Sie einen Screenshot in der
            höchstmöglichen Auflösung Ihres Geräts. Alternativ können Sie
            Photoshop oder die kostenlose App Theta+ (iOS/Android) verwenden, um
            flache Ausschnitte in benutzerdefinierten Auflösungen zu
            extrahieren. Instagrams optimales Seitenverhältnis ist 4:5
            (Hochformat) oder 1:1 (Quadrat).
          </p>

          <h3>Vorschaubilder für Link-Shares</h3>
          <p>
            Wenn Sie einen Virtual-Tour-Link auf einer beliebigen Plattform
            teilen (LinkedIn, Twitter, E-Mail), ist das Vorschaubild
            (Open-Graph-Bild) das, was die Klickrate bestimmt. Stellen Sie
            sicher, dass Ihre Tour-Plattform ein gut eingestelltes Vorschaubild
            hat – in Travvir legen Sie das Tour-Vorschaubild in den
            Tour-Einstellungen auf die überzeugendste Einzelansicht des Raums
            fest. Ein überzeugendes Vorschaubild erhöht die Link-Klickrate um
            <strong>35–55 %</strong> im Vergleich zu einer auto-generierten
            Vorschau.
          </p>
        </section>

        <section>
          <h2>Social-Media-Inhaltsstrategie für 360°-Touren</h2>

          <h3>Facebook-Posting-Best-Practices</h3>
          <p>
            Posten Sie 360°-Fotos als eigenständige Posts (nicht in Alben) für
            maximales Engagement – Album-Posts aktivieren den interaktiven
            360°-Viewer nicht. Schreiben Sie Bildunter­schriften, die Zuschauer
            explizit darauf hinweisen, dass sie zum Umschauen ziehen können:
            „Ziehen Sie, um unser neues [Raumname] zu erkunden ↔️" erzielt
            deutlich bessere Ergebnisse als Bildunter­schriften, die die
            Interaktivität nicht erwähnen. Posten Sie zu Hauptengagement-Zeiten:
            Dienstag–Donnerstag 10:00–14:00 Uhr erzeugt in der Regel die höchste
            Reichweite für Unternehmensseiten.
          </p>

          <h3>Plattform­übergreifender Verteilvungs-Workflow</h3>
          <p>
            Ein einziges 360°-Foto-Shooting liefert Inhalte für eine
            plattform­übergreifende Kampagne: (1) Das vollständige
            äquirektanguläre Bild direkt auf Facebook für interaktives
            Engagement hochladen. (2) Flache Ausschnitte für
            Instagram-Feed-Posts exportieren, mit Bio-Link zur vollständigen
            Tour. (3) Ein 15–30-Sekunden-Bildschirm­aufnahme- Video für
            Instagram Reels und TikTok erstellen. (4) Die Tour-URL auf LinkedIn
            mit Kontext über Ihr Unternehmen oder Objekt teilen. (5) Zum Google
            Business Profil für lokale Suchsichtbarkeit hinzufügen.
          </p>
          <p>
            Dieser Mehrkanalansatz aus einer einzigen Foto-Session maximiert den
            Return on Investment der Zeit, die Sie in die Erstellung der
            360°-Inhalte investiert haben.
          </p>
        </section>

        <section>
          <h2>Fazit: Format der Plattform anpassen</h2>
          <p>
            Das Teilen von 360°-Fotos ist kein Einheitsverfahren. Facebook
            liefert das vollständige interaktive Erlebnis direkt im Feed.
            Instagram erfordert eine Vorschau-basierte Strategie mit Links zur
            vollständigen Tour. YouTube funktioniert für 360°-Video-Inhalte. Das
            Google Business Profil bietet den unmittelbarsten kommerziellen
            Nutzen für lokale Unternehmen.
          </p>
          <p>
            Planen Sie Ihre Verteilungsstrategie für jede Plattform vor dem
            Shooting, und Sie maximieren die Reichweite Ihrer 360°-Inhalte über
            alle Kanäle, die Ihr Publikum nutzt.
          </p>
        </section>
      
        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li><a href="/blog/de/was-ist-ein-360-foto">Was ist ein 360°-Foto? Vollständige Erklärung</a></li>
            <li><a href="/blog/de/360-fotos-mit-smartphone-aufnehmen">360°-Fotos mit Smartphone : Vollständige Anleitung</a></li>
            <li><a href="/blog/de/360-virtual-tour-website-einbetten">360°-Virtual-Tour in Website einbetten</a></li>
            <li><a href="/blog/de/360-virtual-tour-bildgroesse">360°-Virtual-Tour-Bildgröße : Optimale Einstellungen</a></li>
            <li><a href="/blog/de/virtual-tour-seo-leitfaden">Virtual Tour SEO : Rankings verbessern</a></li>
            <li><a href="/blog/de/360-virtual-tour">360° Virtual Tour : Der komplette Leitfaden</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "360-foto-soziale-medien-teilen",
);

useBlogSeo({
  title: "360°-Foto in sozialen Medien teilen [2026] | Travvir",
  description:
    "So teilen Sie 360°-Fotos auf Instagram, Facebook & YouTube. Schritt-für-Schritt-Anleitung für maximale Reichweite und Engagement.",
  slug: "360-foto-soziale-medien-teilen",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
